import React, { PureComponent, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Skeleton from 'react-skeleton-loader';
import { Alert } from 'reactstrap';
import { connect } from 'react-redux';
import { DataTable, DeleteButton, DownloadButton, FileMetadata, SendButton } from '../';
import {
  AUTO_GENERATED_FILE_CATEGORIES, NOT_REQUIRED_STEP_NUMBER,
  PENDING_STEP_NUMBER, SIGNED_STEP_NUMBER,
} from '../../constants';
import { deleteSignatureRequest } from '../../modules';

class EmployeeDocumentsTable extends PureComponent {
  static buildHeaders(isManagerOrAbove) {
    const headers = [
      { classes: ['border-0'], content: () => <FormattedMessage id="DOCUMENT" /> },
      { classes: ['border-0'], content: () => <FormattedMessage id="CATEGORY" /> },
      // any situation where we want to exclude this?
      { classes: ['border-0'], content: () => <FormattedMessage id="SIGNATURE_STATUS" /> },
      { classes: ['border-0', 'd-none', 'd-sm-block'], content: '' },
    ];

    if (isManagerOrAbove) {
      const additionalHeaders = [
        { classes: ['border-0'], content: () => <FormattedMessage id="EMPLOYEE" /> },
        { classes: ['border-0'], content: () => <FormattedMessage id="COMPANY" /> },
      ];

      headers.splice(2, 0, ...additionalHeaders);
    }

    return headers;
  }

  static filteredRowExists(item, filter) {
    if (!item && !filter) {
      return false;
    }

    const values = this.getValuesFromObject(item);
    for (let i = 0; i < values.length; i += 1) {
      if (values[i].toLowerCase().includes(filter.toLowerCase())) {
        return true;
      }
    }

    return false;
  }

  static getValuesFromObject(object) {
    const values = Object.values(object);
    let stringValues = [];
    values.forEach((val) => {
      if (val !== null) {
        if (typeof val === 'string') {
          stringValues.push(val);
        } else if (typeof val === 'object') {
          const childObjectValues = this.getValuesFromObject(val);
          stringValues = stringValues.concat(childObjectValues);
        }
      }
    });
    return stringValues;
  }

  buildRows() {
    const {
      data,
      downloadAction,
      downloadUrlLoading,
      currentDocDownloadId,
      docFilter,
      isManagerOrAbove,
      isAdminOrAbove,
      toggleSendReminderModal,
    } = this.props;

    if (data.length === 0) {
      const skeletonCount = 30;
      const skeletonConfig = {
        width: '50%',
        borderRadius: '2px',
        color: '#ededed',
        widthRandomness: 0.2,
        height: '18px',
      };
      let i = 0;
      const skeletonLoaders = new Map();
      while (i < skeletonCount) {
        i += 1;
        const skeletonStyle = [
          {
            content: () => (<Fragment><Skeleton {...skeletonConfig} /><br /><Skeleton {...skeletonConfig} /></Fragment>),
          },
          {
            classes: ['d-flex', 'align-items-center'],
            content: () => (<Skeleton {...{ ...skeletonConfig, width: '100px' }} />),
          },
          {
            classes: ['d-sm-flex', 'd-none', 'align-items-center', 'justify-content-center'],
            content: () => (
              <Fragment>
                <Skeleton {...{ ...skeletonConfig, width: '16px', height: '20px' }} />
                <Skeleton {...{ ...skeletonConfig, width: '16px', height: '20px' }} />
                <Skeleton {...{ ...skeletonConfig, width: '16px', height: '20px' }} />
              </Fragment>
            ),
          },
        ];

        if (isManagerOrAbove) {
          const additionalColumnsSkeletonStyle = new Array(2).fill({
            classes: ['d-flex', 'align-items-center'],
            content: () => (<Skeleton {...{ ...skeletonConfig, width: '100px' }} />),
          });

          skeletonStyle.splice(2, 0, ...additionalColumnsSkeletonStyle);
        }

        skeletonLoaders.set(i, skeletonStyle);
      }
      return skeletonLoaders;
    }
    const rows = new Map();
    data.forEach((item) => {
      const {
        id,
        companyId,
        employeeId,
        title,
        category,
        uploadDate,
        esignDate,
        companyName,
        employeeName,
        emailAddress,
        employeeCode,
        isOnboarding,
        isPublishedToEmployee,
        uploadedBy,
        status,
        isHelloSignDocument,
      } = item;
      const isDeleteButtonDisabled =
      ((status && status.stepNumber === NOT_REQUIRED_STEP_NUMBER) && isPublishedToEmployee) ||
      ((status && status.stepNumber === PENDING_STEP_NUMBER) && !isAdminOrAbove) ||
      isOnboarding ||
      (status && status.stepNumber === SIGNED_STEP_NUMBER) ||
      (AUTO_GENERATED_FILE_CATEGORIES.filter(fileCategory => fileCategory === category).length > 0 &&
      uploadedBy === 'Onboarding') ||
      (category === 'I-9' && title === 'FormI9') ||
      // TODO: MJ-7834: employees should be able to delete documents they uploaded. uploadedBy uses user name,
      // so we should add an uploadedByEmployee flag to check off of
      !isAdminOrAbove;
      const isDownloadButtonDisabled = status.stepNumber === PENDING_STEP_NUMBER || status.isProcessing === true;
      let deleteQuestionDetail = 'THIS_ACTION_CANNOT_BE_UNDONE';
      let deleteAction = null;
      if (status.stepNumber === PENDING_STEP_NUMBER) {
        deleteQuestionDetail = isHelloSignDocument ? 'DELETE_PENDING_ENHANCED_EE_DOC_DETAIL' : 'DELETE_PENDING_EE_DOC_DETAIL';
        deleteAction = () => { this.props.deleteSignatureRequest(companyId, employeeId, id); };
      }

      if (EmployeeDocumentsTable.filteredRowExists(item, docFilter, status)) {
        const downloadClasses = ['btn-download', 'row-action'];

        if (downloadUrlLoading === 'loading' && id === currentDocDownloadId) {
          downloadClasses.push('loading');
        }
        const rowData = [
          {
            content: () => (
              <Fragment>
                <span className="text-primary text-break">{title}</span><br />
                <FileMetadata
                  key={`metadata-${id}`}
                  data={
                    [
                      {
                        value: uploadDate,
                        formatString: 'UPLOADED',
                      },
                      {
                        value: esignDate,
                        formatString: 'ESIGNED_ON',
                      },
                    ]
                  }
                />
              </Fragment>
            ),
          },
          {
            classes: ['d-flex', 'align-items-center'],
            content: () => <span className="text-capitalize">{category}</span>,
          },
          {
            classes: ['d-flex', 'align-items-center'],
            // TODO: Replace this with strata component once it is merged (SDS-20)
            content: () => {
              if (status.stepNumber === PENDING_STEP_NUMBER) {
                return <mark className="px-2 py-1 text-muted rounded">{status.name}</mark>;
              } else if (status.stepNumber === SIGNED_STEP_NUMBER) {
                return <mark className="px-2 py-1 text-muted rounded" style={{ backgroundColor: '#D7F1E0' }}>{status.name}</mark>;
              } else {
                return <mark className="px-2 py-1 text-muted rounded" style={{ backgroundColor: '#E0E0E0' }}>{status.name}</mark>;
              }
            },
          },
          {
            classes: ['d-md-flex', 'd-none', 'align-items-center', 'justify-content-center'],
            content: () => (
              <Fragment>
                <div className="d-flex flex-row">
                  <SendButton
                    id={id}
                    uniqueId="send-reminder"
                    onClick={() => {
                      document.activeElement.blur();
                      toggleSendReminderModal(id);
                    }}
                    showButton={(status.stepNumber === PENDING_STEP_NUMBER) && isManagerOrAbove}
                    isDisabled={!emailAddress}
                    tooltipText={!emailAddress ? 'EMPLOYEE_MISSING_EMAIL_TOOLTIP' : 'SEND_A_REMINDER'}
                  />
                  <DownloadButton
                    downloadAction={() => downloadAction(id)}
                    classes={downloadClasses}
                    color="link"
                    isDisabled={isDownloadButtonDisabled}
                  />
                  <DeleteButton
                    id={id}
                    additionalInfo={{
                    companyId,
                    employeeId,
                  }}
                    uniqueId="delete-document"
                    outsideClickIgnoreClass="delete-tooltip"
                    deleteQuestion="DELETE_DOCUMENT_QUESTION"
                    deleteQuestionDetail={deleteQuestionDetail}
                    deleteAction={deleteAction}
                    disabled={{
                    isDisabled: isDeleteButtonDisabled,
                    toolTipMessage: 'DOCUMENT_TYPE_CANNOT_BE_DELETED_MESSAGE',
                  }}
                  />
                </div>
              </Fragment>
            ),
          },
        ];

        let employeeMetadata = employeeName;
        if (employeeName && employeeCode) {
          employeeMetadata = `${employeeName}(${employeeCode})`;
        }

        if (isManagerOrAbove) {
          const additionalRowData = [
            {
              classes: ['d-flex', 'align-items-center'],
              content: () => <span>{employeeMetadata}</span>,
            },
            {
              classes: ['d-flex', 'align-items-center'],
              content: () => <span>{companyName}</span>,
            },
          ];

          rowData.splice(2, 0, ...additionalRowData);
        }

        rows.set(id, rowData);
      }
    });
    return rows;
  }

  noResultsAlert = () => (
    <Alert color="info" className="mt-3 mb-0">
      {<FormattedMessage id="NO_DOCUMENTS_MATCH" />} {` "${this.props.docFilter}".`}
    </Alert>
  )

  render() {
    const { isManagerOrAbove, rowAction } = this.props;
    const headings = EmployeeDocumentsTable.buildHeaders(isManagerOrAbove);
    const rows = this.buildRows();
    const columnClasses = isManagerOrAbove ?
      [
        'col-4 col-sm-3 ',
        'col-4 col-sm-2 col-md-2',
        'col-2 ',
        'col-2 ',
        'col-2 ',
        'col-1 ',
      ]
      :
      [
        'col-4 ',
        'col-4 col-sm-2 col-md-2',
        'col-4',
        '      col-sm-2 col-md-2',
      ];
    const alert = (rows && rows.size) || (this.props.filtering) || (this.props.getDocsLoading === 'loading') ?
      null
      :
      this.noResultsAlert();
    return (
      <React.Fragment>
        <div className="employee-documents-table">
          <DataTable
            headings={headings}
            rows={rows}
            rowAction={rowAction}
            columnClasses={columnClasses}
          />
        </div>
        {alert}
      </React.Fragment>
    );
  }
}

EmployeeDocumentsTable.defaultProps = {
  downloadAction: undefined,
  getDocsLoading: '',
  downloadUrlLoading: '',
  currentDocDownloadId: undefined,
  docFilter: '',
  filtering: false,
  isManagerOrAbove: false,
  isAdminOrAbove: false,
  toggleSendReminderModal: () => {},
};

EmployeeDocumentsTable.propTypes = {
  rowAction: PropTypes.func.isRequired,
  toggleSendReminderModal: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.shape({
    employeeDocs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      companyId: PropTypes.number,
      employeeId: PropTypes.number,
      title: PropTypes.string,
      category: PropTypes.string,
      uploadDate: PropTypes.string,
      isEsignatureDocument: PropTypes.string,
      isHelloSignTemplate: PropTypes.bool,
      isOnboarding: PropTypes.bool,
      status: PropTypes.shape({
        name: PropTypes.string,
        priority: PropTypes.number,
        stepNumber: PropTypes.number,
        isProcessing: PropTypes.bool,
      }),
    })),
  })).isRequired,
  downloadAction: PropTypes.func,
  getDocsLoading: PropTypes.string,
  downloadUrlLoading: PropTypes.string,
  currentDocDownloadId: PropTypes.string,
  docFilter: PropTypes.string,
  filtering: PropTypes.bool,
  isManagerOrAbove: PropTypes.bool,
  isAdminOrAbove: PropTypes.bool,
  deleteSignatureRequest: PropTypes.func.isRequired,
};

export default connect(
  () => ({}),
  { deleteSignatureRequest }
)(hot(module)(EmployeeDocumentsTable));



// WEBPACK FOOTER //
// ./src/components/EmployeeDocumentsTable/EmployeeDocumentsTable.jsx